import { ReactNode } from 'react';

import ReactHtmlParser from 'react-html-parser';
import { useNavigate, useParams } from 'react-router';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Button, Card, Chip, Stack, styled, Typography } from '@mui/material';

import StatusChip from '../../../components/chips/StatusChip';
import { ConfirmDialog } from '../../../components/dialogs';
import { useDeactivateReportedComment, useGetReportedCommentByPostId, useReinstateReportedComment } from '../../../lib/comment/hooks';
import { useDeleteComment } from '../../../lib/comment/hooks/useDeleteComment';
import { extractImagesFromPostContent, getArticleUrl, getAuthorName } from '../../../lib/util/helpers';
import { CommentReport } from '../../../types/CommentReport';

function ReportedCommentEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const commentId = id ?? '';
  const getReportedCommentById = useGetReportedCommentByPostId(commentId);
  const { mutateAsync: deleteComment, isLoading: isLoadingDeleteComment } = useDeleteComment();
  const getReportedComments = getReportedCommentById.data as CommentReport[] | undefined;
  let ready = false;

  ready = !getReportedCommentById.isLoading;

  const postData = (data: any) => {
    const { images, cleanedContent } = extractImagesFromPostContent(data);

    return (
      <>
        <Box sx={{ display: 'flex', gap: '24px' }}>
          {images.map((content, index) => (
            <Box
              key={index}
              component="img"
              sx={{
                width: '250px',
                height: '250px',
                objectFit: 'cover',
              }}
              alt="Comment Image"
              src={content.src}
            />
          ))}
        </Box>
        {ReactHtmlParser(cleanedContent)}
      </>
    );
  };

  const deactivateComment = useDeactivateReportedComment();
  const reinstateComment = useReinstateReportedComment();

  const handleDeactivate = async (id: string) => {
    await deactivateComment.mutate({ reportedCommentId: id });
    navigate(-1);
  };

  const handleReinstate = async (id: string) => {
    await reinstateComment.mutate(id);
    setTimeout(() => {
      navigate(-1);
    }, 1000);
  };

  return (
    <>
      {ready && getReportedComments && (
        <Container>
          <TopBar>
            <Typography variant="h5">Reported Comment Detail </Typography>
          </TopBar>

          <StyledCard>
            <Stack direction="row" spacing={2} mb={4}>
              {/* First Column */}
              <Stack spacing={2} display="flex" width="100%">
                <DisplayData label="Article Title" data={getReportedComments[0]?.comment.post.title} />
                <Button
                  onClick={() => {
                    const post = getReportedComments[0]?.comment.post;
                    if (!post) return;
                    const url = getArticleUrl(post);
                    window.open(url, '_blank', 'noopener,noreferrer');
                  }}
                  type="button"
                  variant="contained"
                >
                  <OpenInNewIcon fontSize="small" sx={{ marginRight: '8px' }} />
                  Visit Article
                </Button>

                <Typography fontWeight="bold">Comment Details:</Typography>
                <Stack paddingLeft={2} spacing={2} width="100%" sx={{ borderLeft: '4px solid grey', backgroundColor: '#f9f9f9' }}>
                  <DisplayData label="Comment" data={postData(getReportedComments[0]?.comment.message)} />

                  <DisplayData label="Comment Author" data={getAuthorName(getReportedComments[0]?.comment.user)} />

                  <DisplayData label="Created" data={new Date(getReportedComments[0]?.comment.created).toLocaleDateString()} />
                </Stack>
              </Stack>

              {/* Second Column */}
              <Stack spacing={2} display="flex" width="100%">
                <DisplayData label="Reported by Admin" data={getReportedComments[0]?.comment.isReportedByAdmin ? 'Yes' : 'No'} />

                <DisplayData
                  label="Status"
                  data={
                    getReportedComments[0]?.comment.deactivated ? <StatusChip deactivated /> : <Chip label="Reported" color="warning" />
                  }
                />

                <DisplayData label="# of Reports" data={getReportedComments.length.toString()} />
              </Stack>
            </Stack>

            <ButtonContainer>
              <Buttons>
                <Button onClick={() => navigate(-1)} type="button" size="large">
                  Cancel
                </Button>
                <ConfirmDialog
                  trigger={(open) => (
                    <Button
                      onClick={open}
                      type="button"
                      variant="contained"
                      size="large"
                      disabled={getReportedComments[0]?.comment.deactivated ? true : false}
                    >
                      {getReportedComments[0]?.comment.deactivated ? 'Deactivated' : 'Deactivate'}
                    </Button>
                  )}
                  title="Deactivate Reported Post Comment"
                  description="Are you sure you want to deactivate this reported post comment?"
                  cancelBtnText={'Cancel'}
                  submitBtnText={'Submit'}
                  onConfirm={async (close) => {
                    await handleDeactivate(getReportedComments[0]?.comment.id);
                    close();
                  }}
                />
                {getReportedComments[0]?.comment.deactivated && (
                  <ConfirmDialog
                    trigger={(open) => (
                      <Button onClick={open} type="button" variant="contained" size="large">
                        Reinstate
                      </Button>
                    )}
                    title="Reinstate Deactivated Comment"
                    description="Are you sure you want to reinstate this deactivated comment?"
                    cancelBtnText={'Cancel'}
                    submitBtnText={'Submit'}
                    onConfirm={async (close) => {
                      await handleReinstate(getReportedComments[0]?.comment.id);
                      close();
                    }}
                  />
                )}

                <ConfirmDialog
                  trigger={(open) => (
                    <Button onClick={open} type="button" variant="contained" color="error" size="large">
                      Delete
                    </Button>
                  )}
                  title="Delete Comment"
                  description="Are you sure you want to delete this comment?"
                  cancelBtnText={'Cancel'}
                  submitBtnText={'Submit'}
                  onConfirm={async (close) => {
                    await deleteComment(getReportedComments[0]?.comment.id);
                    close();
                    navigate(-1);
                  }}
                  disabled={isLoadingDeleteComment}
                  loading={isLoadingDeleteComment}
                />
              </Buttons>
            </ButtonContainer>
          </StyledCard>

          <StyledCard>
            <Stack spacing={2}>
              <Typography fontWeight="bold">Reports</Typography>

              {getReportedComments.map((reportedComment: CommentReport, index: number) => {
                const reporter =
                  reportedComment?.reporter.firstName && reportedComment?.reporter.lastName
                    ? `${reportedComment?.reporter.firstName} ${reportedComment?.reporter.lastName}`
                    : reportedComment?.reporter.username;
                return (
                  <Stack
                    sx={{ borderTop: '1px solid rgba(224, 224, 224, 1)', background: 'rgba(240, 240, 240, 1)', paddingTop: '16px' }}
                    key={index}
                    spacing={1}
                    padding={2}
                  >
                    <Typography>Reported Date: {new Date(reportedComment?.created).toLocaleDateString()}</Typography>
                    <Typography>Reporter Username: {reporter} </Typography>
                    <Typography>Reason: {reportedComment?.reason}</Typography>
                    <Typography>Description: {reportedComment?.description}</Typography>
                  </Stack>
                );
              })}
            </Stack>
          </StyledCard>
        </Container>
      )}
    </>
  );
}

function DisplayData({ label, data }: { label: string; data: string | ReactNode }) {
  return (
    <Stack>
      <Typography fontWeight="bold">{label}:</Typography>
      <Typography>{data}</Typography>
    </Stack>
  );
}

const Container = styled('div')({
  padding: '20px',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
});

const TopBar = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  margin: '8px 0',
  justifyContent: 'space-between',
});

const StyledCard = styled(Card)({
  padding: '2rem',
});

const ButtonContainer = styled('div')({
  marginTop: '40px',
  alignSelf: 'flex-end',
  flex: 1,
  display: 'flex',
  justifyContent: 'flex-end',
});

const Buttons = styled('div')({
  display: 'flex',
  justifyContent: 'flex-start',
  gap: '7px',
  '& > *': {
    margin: '5px',
  },
});

export default ReportedCommentEdit;
